import MapPlugin from "../MapPlugin";
import Route from "../../../../../common/Route";
import { Checkbox, FormControlLabel, FormGroup, Stack } from "@mui/material";
import LayerSettings from "../../common/LayerSettings";
import { useCallback, useEffect, useState } from "react";
import { useOperationsMap } from "../../OperationsMapProvider";
import { useTranslation } from "react-i18next";
import { EntityFetcher } from "@emberly/zenith-client";
import { GeoJsonPoint } from "../../../../../common/maphelpers";


export default class MyLocationsLayer extends MapPlugin {

  constructor(map) {
    super("mylocations", map);

    this.defaultSettings = {
      warehouses: false,
      startlocations: false,
      stations: true,
      customers: false,
      contacts: false
    };

    this._timer = null;
    this.addImage("station");
    this.addImage("garage");
    this.addImage("flag");
    this.addImage("face");
    this.addImage("store");
  }

  async onMount() {
    try {
      const { warehouses, startlocations, contacts, customers } = await this.getSources();
      const p = this.theme.palette;

      this._map.addSource("stations", {
        type: "geojson",
        data: Route.GeoJson([
          GeoJsonPoint(
            this.station.location.coordinates,
            {
              title: this.station.displayName,
              icon: "station",
              color: p.success.light
            }
          )
        ])
      })

      this._map.addSource("warehouses", {
        type: "geojson",
        data: Route.GeoJson(warehouses)
      });

      this._map.addSource("startlocations", {
        type: "geojson",
        data: Route.GeoJson(startlocations)
      });

      this._map.addSource("contacts", {
        type: "geojson",
        data: Route.GeoJson(contacts),
        cluster: true,
        clusterRadius: 25,
      });

      this._map.addSource("customers", {
        type: "geojson",
        data: Route.GeoJson(customers),
        cluster: true,
        clusterRadius: 25,
      });

      this.addLayers("startlocations");
      this.addLayers("warehouses");
      this.addLayers("stations");
      this.addLayers("contacts", true);
      this.addLayers("customers", true);

      this._timer = setInterval(() => this.updateSources(), 1000 * 60 * 15);

    } catch (err) {
      console.log(err);
    }
  }

  addLayers(name, cluster = false) {
    const p = this.theme.palette;

    // statlocations layers
    this._map.addLayer({
      id: `${name}-circle`,
      source: name,
      slot: "elevation-2",
      type: "circle",
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-radius": 16,
        "circle-color": ["get", "color"],
        "circle-stroke-width": 2,
        "circle-stroke-color": "#FFF",
      }
    });

    this._map.addLayer({
      id: `${name}-symbol`,
      source: name,
      slot: "elevation-2",
      type: "symbol",
      filter: ["!", ["has", "point_count"]],
      "layout": {
        "icon-image": ["get", "icon"],
        "icon-size": 1.6,
        "icon-ignore-placement": true,
        "text-allow-overlap": true,
        "text-ignore-placement": true,
        "text-field": ["get", "title"],
        "text-font": [
          "Open Sans Semibold",
          "Arial Unicode MS Bold"
        ],
        "text-size": 12,
        "text-offset": [-0.1, -1.6],
        "text-anchor": "bottom"
      },
      "paint": {
        "icon-color": "#FFF",
        "text-halo-color": "#fff",
        "text-halo-width": 2
      }
    });

    if (cluster) {

      // Add cluster layers
      this._map.addLayer({
        id: `${name}-cluster-circle`,
        source: name,
        slot: "elevation-2",
        type: "circle",
        filter: ["has", "point_count"],
        paint: {
          "circle-stroke-width": 2,
          "circle-stroke-color": "#FFF",
          "circle-color": p.info.light,
          "circle-radius": [
            "step",
            ["get", "point_count"],
            18,
            100,
            30,
            750,
            40
          ]
        }
      });

      this._map.addLayer({
        id: `${name}-cluster-text`,
        source: name,
        slot: "elevation-2",
        type: "symbol",
        filter: ["has", "point_count"],
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
          "text-size": 14,
        },
        paint: {
          "text-color": "#FFF"
        }
      });
    }

    this.updateLayerSettings(name, cluster);
  }

  updateLayerSettings(name, cluster = false) {
    this._map.setLayoutProperty(`${name}-circle`, "visibility", !!this.settings[name] ? "visible" : "none");
    this._map.setLayoutProperty(`${name}-symbol`, "visibility", !!this.settings[name] ? "visible" : "none");

    if (cluster) {
      this._map.setLayoutProperty(`${name}-cluster-circle`, "visibility", !!this.settings[name] ? "visible" : "none");
      this._map.setLayoutProperty(`${name}-cluster-text`, "visibility", !!this.settings[name] ? "visible" : "none");
    }

    // TODO hide clusters etc

  }

  removeLayers(name, cluster = false) {
    this._map.removeLayer(`${name}-circle`);
    this._map.removeLayer(`${name}-symbol`);

    if (cluster) {
      this._map.removeLayer(`${name}-cluster-circle`);
      this._map.removeLayer(`${name}-cluster-text`);
    }
  }

  onUnMount() {
    clearInterval(this._timer);

    this.removeLayers("startlocations");
    this.removeLayers("warehouses");
    this.removeLayers("stations");
    this.removeLayers("contacts", true);
    this.removeLayers("customers", true);


    this._map.removeSource("stations");
    this._map.removeSource("warehouses");
    this._map.removeSource("startlocations");
    this._map.removeSource("contacts");
    this._map.removeSource("customers");
  }

  async getSources() {
    const entities = new EntityFetcher(() => this.getAccessToken());
    const p = this.theme.palette;

    const [warehouses, startlocations, contacts] = await Promise.all([
      this.settings.warehouses ? entities.fetch("Warehouse") : null,
      this.settings.startlocations ? entities.fetch("StartLocation") : null,
      this.settings.contacts || this.settings.customers ? entities.fetch("Contact") : null,
    ]);

    return {
      warehouses: warehouses?.results?.map(
        t => GeoJsonPoint(
          t.location.coordinates,
          {
            title: t.name,
            color: p.info.light,
            icon: "garage"
          }
        )
      ) || [],
      startlocations: startlocations?.results?.map(
        t => GeoJsonPoint(
          t.location.coordinates,
          {
            title: t.name,
            color: p.info.light,
            icon: "flag"
          }
        )
      ) || [],
      contacts: contacts?.results?.filter(t => !t.isCustomer && t?.location?.coordinates?.length === 2).map(
        t => GeoJsonPoint(
          t.location.coordinates,
          {
            title: t.name,
            color: p.info.dark,
            icon: "face"
          }
        )
      ) || [],
      customers: contacts?.results?.filter(t => t.isCustomer && t?.location?.coordinates?.length === 2).map(
        t => GeoJsonPoint(
          t.location.coordinates,
          {
            title: t.name,
            color: p.info.dark,
            icon: "store"
          }
        )
      ) || [],
    };
  }

  async updateSources() {
    if (this.destroyed) return;

    try {
      const { warehouses, startlocations, contacts, customers } = await this.getSources();


      this._map.getSource("warehouses")?.setData(Route.GeoJson(warehouses));
      this._map.getSource("startlocations")?.setData(Route.GeoJson(startlocations));
      this._map.getSource("contacts")?.setData(Route.GeoJson(contacts));
      this._map.getSource("customers")?.setData(Route.GeoJson(customers));

    } catch (err) {
      console.log(err);
    }
  }

  onUpdateSettings(_, update) {

    if (update.customers || update.contacts || update.warehouses || update.startlocations) {
      this.updateSources();
    }

    this.updateLayerSettings("stations");
    this.updateLayerSettings("startlocations");
    this.updateLayerSettings("warehouses");
    this.updateLayerSettings("contacts", true);
    this.updateLayerSettings("customers", true);
  }

}



export function MyLocationsLayerSettings() {
  const { t } = useTranslation();
  const { operationsMap } = useOperationsMap();
  const settings = operationsMap.getSettings("mylocations");

  const [stations, setStations] = useState(settings.stations);
  const [warehouses, setWarehouses] = useState(settings.warehouses);
  const [startLocations, setStartLocations] = useState(settings.startlocations);
  const [contacts, setContacts] = useState(settings.contacts);
  const [customers, setCustomers] = useState(settings.customers);
  const [active, setActive] = useState(operationsMap.isPluginActive("mylocations"));

  useEffect(() => {
    const fn = () => {
      setStations(settings.stations);
      setWarehouses(settings.warehouses);
      setStartLocations(settings.startlocations);
      setContacts(settings.contacts);
      setCustomers(settings.customers);
      setActive(operationsMap.isPluginActive("mylocations"));
    };

    operationsMap.on("reset", fn);

    return () => operationsMap.off("reset", fn);
  }, [operationsMap, settings]);


  // Handle plugin active state
  const onHandleAccordion = useCallback((_, val) => {
    setActive(val);

    if (val) {
      operationsMap.activatePlugin("mylocations");
    } else {
      operationsMap.deactivatePlugin("mylocations");
    }
  }, [operationsMap]);

  const onHandleStations = useCallback((ev) => {
    const val = ev.target.checked;
    setStations(val);
    operationsMap.updateSettings("mylocations", { stations: val });
  }, [operationsMap]);

  const onHandleWarehouses = useCallback((ev) => {
    const val = ev.target.checked;
    setWarehouses(val);
    operationsMap.updateSettings("mylocations", { warehouses: val });
  }, [operationsMap]);

  const onHandleStartLocations = useCallback((ev) => {
    const val = ev.target.checked;
    setStartLocations(val);
    operationsMap.updateSettings("mylocations", { startlocations: val });
  }, [operationsMap]);

  const onHandleContacts = useCallback((ev) => {
    const val = ev.target.checked;
    setContacts(val);
    operationsMap.updateSettings("mylocations", { contacts: val });
  }, [operationsMap]);

  const onHandleCustomers = useCallback((ev) => {
    const val = ev.target.checked;
    setCustomers(val);
    operationsMap.updateSettings("mylocations", { customers: val });
  }, [operationsMap]);




  return (
    <LayerSettings title={t("operationsMap:layers:mylocations:title")} open={active} onChange={onHandleAccordion}>
      <Stack spacing={1}>

        <FormGroup>
          <FormControlLabel control={<Checkbox checked={stations} onChange={onHandleStations} />} label={t("operationsMap:layers:mylocations:showStations")} />
          <FormControlLabel control={<Checkbox checked={warehouses} onChange={onHandleWarehouses} />} label={t("operationsMap:layers:mylocations:showWarehouses")} />
          <FormControlLabel control={<Checkbox checked={startLocations} onChange={onHandleStartLocations} />} label={t("operationsMap:layers:mylocations:showStartLocations")} />
          <FormControlLabel control={<Checkbox checked={contacts} onChange={onHandleContacts} />} label={t("operationsMap:layers:mylocations:showContacts")} />
          <FormControlLabel control={<Checkbox checked={customers} onChange={onHandleCustomers} />} label={t("operationsMap:layers:mylocations:showCustomers")} />
        </FormGroup>

      </Stack>
    </LayerSettings>
  );
}
